import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FilePdf, FileXls, DownloadSimple, CheckSquare, Square } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";

const FLAG_MAP = {
  AKL: "\ud83c\uddf3\ud83c\uddff",
  BKK: "\ud83c\uddf9\ud83c\udded",
  SGN: "\ud83c\uddfb\ud83c\uddf3",
  HAN: "\ud83c\uddfb\ud83c\uddf3",
  TPE: "\ud83c\uddf9\ud83c\uddfc",
  BNE: "\ud83c\udde6\ud83c\uddfa",
  MNL: "\ud83c\uddf5\ud83c\udded",
  NRT: "\ud83c\uddef\ud83c\uddf5",
  KIX: "\ud83c\uddef\ud83c\uddf5",
  ICN: "\ud83c\uddf0\ud83c\uddf7",
  CEB: "\ud83c\uddf5\ud83c\udded",
  LHR: "\ud83c\uddec\ud83c\udde7",
};

export default function Reports() {
  const { api } = useAuth();

  const [routes, setRoutes]             = useState([]);
  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [months, setMonths]             = useState(6);
  const [generating, setGenerating]     = useState(null);
  const [error, setError]               = useState(null);

  useEffect(() => {
    api.get("/routes").then(res => setRoutes(res.data || []));
  }, [api]);

  const toggleRoute = (id) => {
    setSelectedRoutes(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  const selectAll   = () => setSelectedRoutes(routes.map(r => r.id));
  const selectNone  = () => setSelectedRoutes([]);

  const generate = async (format) => {
    if (selectedRoutes.length === 0) {
      setError("Please select at least one route.");
      return;
    }
    setError(null);
    setGenerating(format);

    try {
      const response = await api.post(
        "/reports/export",
        { route_ids: selectedRoutes, format, months },
        { responseType: "blob" },
      );

      const ext      = format === "pdf" ? "pdf" : "xlsx";
      const mime     = format === "pdf"
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url      = window.URL.createObjectURL(new Blob([response.data], { type: mime }));
      const link     = document.createElement("a");
      link.href      = url;
      link.download  = `CI_Fare_Report_${new Date().toISOString().slice(0,10)}.${ext}`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError("Report generation failed. Please try again.");
      console.error(err);
    } finally {
      setGenerating(null);
    }
  };

  return (
    <div data-testid="reports-page" className="p-6 max-w-4xl mx-auto space-y-6">

      <div>
        <h1 className="font-syne font-bold text-2xl text-white tracking-tight">
          Generate Report
        </h1>
        <p className="text-[#8EAADF] text-sm mt-1">
          Select routes and export a detailed fare comparison report in Excel or PDF format.
        </p>
      </div>

      {/* Route selector */}
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="bg-[#0F2044] rounded-xl border border-[rgba(255,255,255,0.08)] p-5"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-syne font-bold text-white text-sm">Select Routes</h2>
          <div className="flex gap-3">
            <button data-testid="select-all-btn" onClick={selectAll}
              className="text-xs text-[#8EAADF] hover:text-white transition-colors">
              Select all
            </button>
            <span className="text-[rgba(255,255,255,0.15)]">|</span>
            <button data-testid="select-none-btn" onClick={selectNone}
              className="text-xs text-[#8EAADF] hover:text-white transition-colors">
              Clear
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {routes.map(route => {
            const selected = selectedRoutes.includes(route.id);
            const originFlag = FLAG_MAP[route.origin_code] || "";
            const destFlag   = FLAG_MAP[route.destination_code] || "";
            return (
              <button
                key={route.id}
                data-testid={`route-select-${route.destination_code}`}
                onClick={() => toggleRoute(route.id)}
                className="flex items-center gap-3 p-3 rounded-lg transition-all text-left"
                style={{
                  background: selected
                    ? "rgba(27,82,196,0.15)"
                    : "rgba(255,255,255,0.03)",
                  border: selected
                    ? "1px solid rgba(27,82,196,0.4)"
                    : "1px solid rgba(255,255,255,0.06)",
                }}
              >
                {selected
                  ? <CheckSquare size={16} weight="fill" color="#1B52C4" />
                  : <Square size={16} color="#8EAADF" />}
                <span className="text-sm text-white font-mono">
                  {originFlag} {route.origin_code} &rarr; {route.destination_code} {destFlag}
                </span>
                <span className="text-xs text-[#8EAADF] ml-auto">
                  {route.destination_city}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-4 pt-4 border-t border-[rgba(255,255,255,0.06)]">
          <p className="text-xs text-[#8EAADF]">
            {selectedRoutes.length} of {routes.length} routes selected
          </p>
        </div>
      </motion.div>

      {/* Options */}
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="bg-[#0F2044] rounded-xl border border-[rgba(255,255,255,0.08)] p-5"
      >
        <h2 className="font-syne font-bold text-white text-sm mb-4">Report Options</h2>
        <div className="flex items-center gap-4">
          <label className="text-xs text-[#8EAADF]">Months of data</label>
          <select
            data-testid="months-select"
            value={months}
            onChange={e => setMonths(Number(e.target.value))}
            className="bg-[rgba(255,255,255,0.06)] border border-[rgba(255,255,255,0.12)] rounded-lg px-3 py-1.5 text-white text-xs"
          >
            <option value={3}>3 months</option>
            <option value={6}>6 months</option>
            <option value={9}>9 months</option>
          </select>
        </div>
      </motion.div>

      {/* Error */}
      {error && (
        <div data-testid="report-error" className="bg-red-900/30 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Export buttons */}
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex gap-4"
      >
        <button
          data-testid="export-excel-btn"
          onClick={() => generate("excel")}
          disabled={!!generating}
          className="flex items-center gap-2 px-5 py-3 rounded-xl font-medium text-sm transition-all disabled:opacity-50"
          style={{ background: "#10B981", color: "#fff" }}
        >
          {generating === "excel"
            ? <><DownloadSimple size={16} className="animate-spin" /> Generating&hellip;</>
            : <><FileXls size={18} weight="duotone" /> Export Excel</>}
        </button>

        <button
          data-testid="export-pdf-btn"
          onClick={() => generate("pdf")}
          disabled={!!generating}
          className="flex items-center gap-2 px-5 py-3 rounded-xl font-medium text-sm transition-all disabled:opacity-50"
          style={{ background: "#E8501A", color: "#fff" }}
        >
          {generating === "pdf"
            ? <><DownloadSimple size={16} className="animate-spin" /> Generating&hellip;</>
            : <><FilePdf size={18} weight="duotone" /> Export PDF</>}
        </button>
      </motion.div>

      {/* What's included */}
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-[#0F2044] rounded-xl border border-[rgba(255,255,255,0.08)] p-5"
      >
        <h2 className="font-syne font-bold text-white text-sm mb-3">What&apos;s included in the report</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-[#8EAADF]">
          {[
            "Executive summary (consolidated routes)",
            "CI competitive position per cabin class",
            "Full fare matrix — all airlines × all months",
            "Competitor gap analysis (NZD + %)",
            "Capacity & market share per route",
            "Route intelligence notes",
            "Data source indicators (Google Flights / Direct)",
            "Colour-coded cells (red = CI undercut, green = CI cheaper)",
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <span style={{ color: "#10B981", fontSize: "10px" }}>&#10003;</span>
              {item}
            </div>
          ))}
        </div>
      </motion.div>

    </div>
  );
}
